import { Outlet, createRootRoute } from "@tanstack/react-router";
import { QueryClientProvider } from "@tanstack/react-query";

import { DatadogRumUser } from "@/components/datadog-rum";
import { ServiceWorkerRegister } from "@/components/service-worker-register";
import { ThemeProvider } from "@/components/theme-provider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/lib/auth";
import { createQueryClient } from "@/lib/query-client";

/**
 * The shell every route renders inside -- the static-SPA equivalent of `src/app/layout.tsx`.
 *
 * Only the providers move here. The `<html>`/`<body>` tags, the metadata and the pre-paint theme
 * script live in `index.html`, because a static shell has no component that owns the document.
 *
 * Provider order is not arbitrary: `AuthProvider` runs `useQuery` internally, so it has to sit
 * inside `QueryClientProvider`, and everything that reads `useAuth` sits inside both.
 *
 * `DatadogRumUser` is mounted here, and RUM's `init` deliberately is *not* -- it lives in
 * `src/main.tsx`, which runs before React, so `window.fetch` is patched before `AuthProvider`
 * fires the first `/v1/me`. This component is separate only because it needs `useAuth`, which
 * means it has to sit inside `AuthProvider`; it must also stay outside `RequireAuth`, since
 * `/setup` is deliberately ungated.
 *
 * `ServiceWorkerRegister` is here for the same reason of needing to run once per app load; it
 * no-ops outside a production build, so `npm run dev` is unaffected.
 *
 * **This comment used to say both `DatadogRum` and `ServiceWorkerRegister` were "deliberately
 * absent -- both read `process.env`, which does not exist under Vite".** That was true when
 * written and then silently stopped being true: both files were ported to `import.meta.env` in a
 * later commit, and nobody removed the note or mounted the components. The result was that RUM
 * collected nothing at all for three days, with no error anywhere -- nothing imported
 * `@datadog/browser-rum`, so the SDK was tree-shaken out of the bundle entirely, and `sw.js`
 * shipped on every deploy while never registering, so `tests/sw.test.mjs` was exercising a file
 * no browser ever ran. "Absent is visible" turned out to be false; a stale reason is worse than
 * no reason, because it reads as a decision somebody already made.
 */
export const Route = createRootRoute({
  component: RootLayout,
});

// Created once at module scope rather than per render. A `QueryClient` built inside the
// component would be replaced on every re-render, throwing away the cache each time -- which
// looks exactly like caching not working rather than like a bug.
const queryClient = createQueryClient();

function RootLayout() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <DatadogRumUser />
        <ServiceWorkerRegister />
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <TooltipProvider>
            <Outlet />
          </TooltipProvider>
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
